'use client';

import { Button } from '@heidi/design-system/button';
import { Dialog, DialogClose, DialogContent } from '@heidi/design-system/dialog';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ArrowLeft, ArrowRight, Download, Loader2, Minus, Plus, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { formFieldEditorMessages } from '../../form-field-editor/form-field-editor.messages';
import { pdfViewerWithFieldsMessages } from '../../form-field-editor/pdf-viewer-with-fields/pdf-viewer-with-fields.messages';
import { fullscreenFormPreviewModalMessages } from './fullscreen-form-preview-modal.messages';

function PdfViewerLoading() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <TypographyCaption>
        <FormattedMessage {...fullscreenFormPreviewModalMessages.loadingPdfViewer} />
      </TypographyCaption>
    </div>
  );
}

const ZoomablePdfViewer = dynamic(() => import('./zoomable-pdf-viewer'), {
  ssr: false,
  loading: () => <PdfViewerLoading />,
});

interface FullscreenFormPreviewModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  pdfUrl: string;
  isLoading?: boolean;
  templateTitle?: string;
  onDownload: () => void;
  isDownloadDisabled?: boolean;
  headerActions?: ReactNode;
  topNavComponent?: ReactNode;
}

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const DEFAULT_ZOOM = 1;

export function FullscreenFormPreviewModal({
  isOpen,
  onOpenChange,
  pdfUrl,
  isLoading,
  onDownload,
  isDownloadDisabled,
  headerActions,
  topNavComponent,
}: Readonly<FullscreenFormPreviewModalProps>) {
  const intl = useIntl();

  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);

  // Reset state when modal opens/closes or URL changes
  useEffect(() => {
    if (isOpen) {
      setPageNumber(1);
      setZoom(DEFAULT_ZOOM);
      setNumPages(0);
    }
  }, [isOpen, pdfUrl]);

  const handleDocumentLoad = useCallback((pages: number) => {
    setNumPages(pages);
  }, []);

  const nextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || prev));
  }, [numPages]);

  const prevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
  }, []);

  const zoomPercentage = Math.round(zoom * 100);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-dvh max-h-dvh w-screen max-w-full flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[95dvh] sm:max-h-[95dvh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg"
        hideCloseIcon
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border p-3 sm:px-6 sm:py-4">
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              aria-label={intl.formatMessage(
                pdfViewerWithFieldsMessages.zoomOutAriaLabel
              )}
            >
              <Minus className="size-4 shrink-0" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetZoom}
              aria-label={intl.formatMessage(
                fullscreenFormPreviewModalMessages.resetZoom
              )}
            >
              <TypographyCaption className="font-medium">
                {zoomPercentage}%
              </TypographyCaption>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              aria-label={intl.formatMessage(pdfViewerWithFieldsMessages.zoomInAriaLabel)}
            >
              <Plus className="size-4 shrink-0" />
            </Button>
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-2 sm:gap-4">
            {topNavComponent ?? headerActions}
            {/* Download Button */}
            <Button
              disableAutomaticSvgSizing
              variant="outline"
              size="md"
              className="flex items-center gap-2"
              onClick={onDownload}
              disabled={!pdfUrl || isDownloadDisabled}
              aria-label={intl.formatMessage(
                fullscreenFormPreviewModalMessages.downloadAria
              )}
              title={intl.formatMessage(fullscreenFormPreviewModalMessages.downloadAria)}
            >
              <Download size={16} />
              <span className="hidden sm:inline">
                <FormattedMessage
                  {...fullscreenFormPreviewModalMessages.downloadButton}
                />
              </span>
            </Button>
            {/* Close Button */}
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={intl.formatMessage(formFieldEditorMessages.close)}
              >
                <X className="shrink-0" />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Content */}
        <div className="relative min-h-0 flex-1">
          {isLoading ? (
            <div className="flex size-full items-center justify-center">
              <Loader2 className="size-8 animate-spin text-text-tertiary" />
            </div>
          ) : (
            <ZoomablePdfViewer
              fileUrl={pdfUrl}
              pageNumber={pageNumber}
              zoom={zoom}
              onDocumentLoad={handleDocumentLoad}
            />
          )}

          {/* Page Navigation - Fixed at bottom */}
          {numPages > 1 && (
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 shadow-lg sm:gap-3 sm:px-4">
              <Button
                variant="ghost"
                size="xs"
                onClick={prevPage}
                disabled={pageNumber <= 1}
                aria-label={intl.formatMessage(
                  pdfViewerWithFieldsMessages.previousPageAriaLabel
                )}
                className={cn(
                  'rounded-md border border-border p-1.5 sm:p-2',
                  pageNumber <= 1 && 'invisible'
                )}
              >
                <ArrowLeft size={12} className="rtl:-scale-x-100" />
              </Button>
              <TypographyCaption className="text-text-secondary">
                <FormattedMessage
                  {...formFieldEditorMessages.formFillingPageOf}
                  values={{ current: pageNumber, total: numPages }}
                />
              </TypographyCaption>
              <Button
                variant="ghost"
                size="xs"
                onClick={nextPage}
                disabled={pageNumber >= numPages}
                aria-label={intl.formatMessage(
                  pdfViewerWithFieldsMessages.nextPageAriaLabel
                )}
                className={cn(
                  'rounded-md border border-border p-1.5 sm:p-2',
                  pageNumber >= numPages && 'invisible'
                )}
              >
                <ArrowRight size={12} className="rtl:-scale-x-100" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
